import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Monitor, Smartphone, Cpu, ShoppingCart, TrendingUp, BookOpen, Globe2, Bot, Briefcase, Megaphone, GraduationCap } from 'lucide-react';
import { getServices } from '../lib/supabase';

const Services = () => {
  const { t, i18n } = useTranslation();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Icon mapping
  const iconMap = {
    Monitor,
    Smartphone, 
    Cpu,
    ShoppingCart,
    TrendingUp,
    BookOpen,
    Globe2,
    Bot,
    Briefcase,
    Megaphone,
    GraduationCap,
    // Lowercase versions for backend compatibility
    monitor: Monitor,
    smartphone: Smartphone,
    cpu: Cpu,
    'shopping-cart': ShoppingCart,
    'trending-up': TrendingUp,
    'book-open': BookOpen
  };

  const gradientMap = {
    consulting_it: 'from-blue-500 to-cyan-500',
    web_dev: 'from-purple-500 to-pink-500',
    mobile_apps: 'from-green-500 to-teal-500',
    ecommerce: 'from-orange-500 to-red-500',
    digital_marketing: 'from-indigo-500 to-purple-500',
    automation_ai: 'from-yellow-500 to-orange-500',
    training: 'from-emerald-500 to-blue-500',
    // Fallback mappings
    consulting: 'from-blue-500 to-cyan-500',
    development: 'from-purple-500 to-pink-500',
    automation: 'from-yellow-500 to-orange-500',
    marketing: 'from-indigo-500 to-purple-500'
  };

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const data = await getServices(i18n.language);
        setServices(data);
        setError(null);
      } catch (err) {
        console.error('Error loading services:', err);
        setError('Erreur lors du chargement des services');
        // Fallback to default services if Supabase fails
        setServices([
          {
            id: 'consulting_it',
            title: i18n.language === 'fr' ? 'Consulting IT' : 'IT Consulting',
            description: i18n.language === 'fr' 
              ? 'Optimisation et accompagnement sur mesure pour votre infrastructure IT'
              : 'Custom optimization and guidance for your IT infrastructure',
            icon: 'Monitor'
          },
          {
            id: 'web_dev',
            title: i18n.language === 'fr' ? 'Développement Web & Mobile' : 'Web & Mobile Development',
            description: i18n.language === 'fr'
              ? 'Sites web et applications mobiles avec React, Flutter, Next.js'
              : 'Websites and mobile apps with React, Flutter, Next.js',
            icon: 'Smartphone'
          },
          {
            id: 'automation_ai',
            title: i18n.language === 'fr' ? 'Automatisation & IA' : 'Automation & AI',
            description: i18n.language === 'fr'
              ? 'Workflows intelligents avec n8n et solutions d\'IA avancées'
              : 'Smart workflows with n8n and advanced AI solutions',
            icon: 'Bot'
          },
          {
            id: 'ecommerce',
            title: i18n.language === 'fr' ? 'E-commerce & Shopify' : 'E-commerce & Shopify',
            description: i18n.language === 'fr'
              ? 'Boutiques en ligne performantes et solutions de vente digitale'
              : 'High-performance online stores and digital sales solutions',
            icon: 'ShoppingCart'
          },
          {
            id: 'digital_marketing',
            title: i18n.language === 'fr' ? 'Marketing Digital' : 'Digital Marketing',
            description: i18n.language === 'fr'
              ? 'SEO, publicité en ligne et stratégies de croissance digitale'
              : 'SEO, online advertising and digital growth strategies',
            icon: 'TrendingUp'
          },
          {
            id: 'training',
            title: i18n.language === 'fr' ? 'Formations & Workshops' : 'Training & Workshops',
            description: i18n.language === 'fr'
              ? 'Formation en technologies, IA et automatisation pour vos équipes'
              : 'Technology, AI and automation training for your teams',
            icon: 'BookOpen'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [i18n.language]);

  if (loading) {
    return (
      <section id="services" className="section-spacing relative">
        <div className="container-custom text-center">
          <div className="flex justify-center items-center py-20">
            <div className="w-12 h-12 border-4 border-secondary-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="services" className="section-spacing relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-secondary-900/5 to-transparent"></div>
      
      <div className="container-custom relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="text-gradient">{t('services.header')}</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {t('services.description')}
          </p>
          {error && (
            <p className="text-yellow-400 text-sm mt-2">
              {error} - Affichage des services par défaut
            </p>
          )}
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon] || Monitor;
            const gradient = gradientMap[service.id] || 'from-blue-500 to-cyan-500';
            
            return (
              <div
                key={service.id}
                className="card-glass card-hover p-8 text-center group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Icon */}
                <div className="relative mb-6">
                  <div className={`w-16 h-16 mx-auto rounded-xl bg-gradient-to-r ${gradient} p-4 group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent className="w-full h-full text-white" />
                  </div>
                  <div className={`absolute inset-0 w-16 h-16 mx-auto rounded-xl bg-gradient-to-r ${gradient} opacity-20 blur-xl group-hover:opacity-40 transition-opacity duration-300`}></div>
                </div>

                {/* Content */}
                <h3 className="text-xl font-semibold mb-4 text-white group-hover:text-gradient transition-colors duration-300">
                  {service.title}
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  {service.description}
                </p>

                {/* Hover Effect */}
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-secondary-500/0 to-primary-500/0 group-hover:from-secondary-500/5 group-hover:to-primary-500/5 transition-all duration-300"></div>
              </div>
            );
          })}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="inline-flex items-center space-x-2 text-secondary-400 mb-4">
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-secondary-400"></div>
            <span className="font-semibold">{t('services.cta')}</span>
            <div className="w-12 h-px bg-gradient-to-l from-transparent to-secondary-400"></div>
          </div>
          <button 
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="btn-primary"
          >
            {t('services.ctaButton')}
          </button>
        </div>
      </div>
    </section>
  );
};

export default Services;